import { AfterViewInit, Component, Input, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

import {
  combineLatest,
  Observable,
  of,
  Subject,
  switchMap,
  Subscription,
  NEVER,
  buffer,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  withLatestFrom,
  take,
  merge,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { CmsCollection, CmsOffer } from '@fcom/core-api';
import {
  ConfigService,
  GlobalBookingTravelClass,
  isDeepEqual,
  isEmpty,
  LocationRouteCffService,
  stopPropagation,
} from '@fcom/core';
import {
  ConsentTextId,
  DestinationClickTarget,
  ElementActions,
  ElementTypes,
  GaContext,
  PageMetaService,
  PriceCalendarCTAParams,
  PriceCalendarParams,
  PriceCalendarService,
  PromotionItem,
  PromotionType,
  RecommendationService,
  TripType,
} from '@fcom/common';
import { DataCloudService } from '@fcom/common/datacloud';
import {
  PersonalizedDestinationLocation,
  TrendingDestinationLocation,
} from '@fcom/common/interfaces/recommendation.interface';
import { GtmService } from '@fcom/common/gtm';
import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';
import { unsubscribe } from '@fcom/core/utils';
import { AmDestinationService } from '@fcom/destination-search/services/am-destination.service';
import { getOfferWithAmDestination } from '@fcom/common/utils';
import { ConsentService } from '@fcom/common/login';

import { BlockOffer, BlockOfferData, MarketingOffer, MarketingOfferPrices } from '../../interfaces';
import { CheapestPriceForAllDestinationsService, toPromoBlock } from '../../services';
import { FetchPriorityEnum } from '../../../ui-components';

const TRENDING_BADGE_LABEL = 'popularNow';

@Component({
  selector: 'fin-trending-destinations-grid',
  template: `
    @let isPersonalizedDestinationsTestVariant = isPersonalizedDestinationsTestVariant$ | async;
    <fin-offer-grid
      *ngIf="originLocationCode$ | async as originLocationCode"
      [title]="
        isPersonalizedDestinationsTestVariant
          ? ('personalizedDestinations.title' | finLocalization)
          : content.teaserTitle
      "
      [offers$]="offers$"
      [cmsTags]="content.subjectTaxonomyTags || []"
      [tripType]="tripType"
      (destinationClicked)="destinationClick(originLocationCode, $event)"
      (scrolledPastDestination)="scrollEvents$.next($event)"
      (flightHotelClicked)="openAmContinueModal(originLocationCode, $event)"
      (calendarClicked)="calendarClicked(originLocationCode, $event)"
      [mobileCarousel]="isPersonalizedDestinationsTestVariant"
      [noHeaderPadding]="isPersonalizedDestinationsTestVariant"
      [fetchImagePriority]="FetchPriorityEnum.low"
    />
    <fin-am-destination-continue-modal
      [open]="isAmContinueModalOpen"
      (closeModal)="closeAmContinueModal()"
      [bookUrl]="flightHotelUrl"
    />
    <fcom-modal
      [open]="(calendarOpen$ | async) !== null"
      [fitToContent]="true"
      [buttons]="null"
      [roundedContent]="true"
      [fullHeight]="true"
      [extraWide]="true"
      (close)="closePriceCalendar()"
      data-testid="price-calendar-modal"
    >
      <fin-price-calendar
        [openWithParams$]="calendarOpen$"
        [showAddReturn]="true"
        [showSubtitle]="true"
        [ctaLabel]="'bookingSearch.search' | finLocalization"
        (modalClose)="closePriceCalendar()"
        (ctaClicked)="priceCalendarCTAClicked($event)"
      />
    </fcom-modal>
  `,
  standalone: false,
})
export class TrendingDestinationsComponent implements OnInit, OnDestroy, AfterViewInit {
  protected readonly FetchPriorityEnum = FetchPriorityEnum;
  private recommendationService = inject(RecommendationService);
  private pageMetaService = inject(PageMetaService);
  private gtmService = inject(GtmService);
  private languageService = inject(LanguageService);
  private cheapestPriceForAllDestinationsService = inject(CheapestPriceForAllDestinationsService);
  private amDestinationService = inject(AmDestinationService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private priceCalendarService = inject(PriceCalendarService);
  private dataCloudService = inject(DataCloudService);
  private consentService = inject(ConsentService);
  private configService = inject(ConfigService);
  private platform = inject(PLATFORM_ID);

  @Input()
  content: CmsCollection;

  offers$: Observable<BlockOfferData[]>;
  scrollEvents$: Subject<[BlockOffer, number]> = new Subject<[BlockOffer, number]>();
  flightHotelUrl: string;
  tripType: TripType;
  originLocationCode$: Observable<string>;
  calendarOpen$: Observable<PriceCalendarParams | null> = NEVER;
  isPersonalizedDestinationsTestVariant$: Observable<boolean>;

  isAmContinueModalOpen = false;

  readonly scrollDebounceTime = 1000;
  readonly popularTrendLimit = 2;
  readonly maxNumberOfOffers = 8;
  readonly minNumberOfOffers = 4;

  private subscriptions = new Subscription();

  constructor() {
    this.calendarOpen$ = this.priceCalendarService.calendarOpen$;
    this.originLocationCode$ = this.pageMetaService.defaultOriginLocationCode$;
  }

  ngOnInit(): void {
    this.tripType = this.getTripType();

    this.isPersonalizedDestinationsTestVariant$ = isPlatformServer(this.platform)
      ? of(false)
      : this.consentService.getCookieConsentStatusById(ConsentTextId.COOKIE_PERSONALIZATION).pipe(
          distinctUntilChanged(),
          withLatestFrom(this.languageService.countryCode),
          switchMap(([consent, country]) =>
            of(consent && this.configService.cfg.isPersonalisedDestinationsEnabled && country === 'FI')
          ),
          finShare()
        );

    this.offers$ = combineLatest([
      this.pageMetaService.defaultOriginLocationCode$.pipe(filter(Boolean)),
      this.cheapestPriceForAllDestinationsService.bothWayOffers(),
      this.isPersonalizedDestinationsTestVariant$,
    ]).pipe(
      switchMap(([origin, offers, isPersonalizedDestinationsTestVariant]) =>
        (isPersonalizedDestinationsTestVariant
          ? this.getPersonalizedDestinations(origin)
          : this.recommendationService.getTrendingDestinations(origin).pipe(catchError(() => of([])))
        ).pipe(
          // filter out current pageMeta location if found and convert first n items to BlockOfferData[]
          map((locations) => {
            const filteredDestination = locations
              .filter((item: TrendingDestinationLocation) => item.locationCode !== origin)
              .map((location: TrendingDestinationLocation) =>
                this.trendingToOffer(location, ...this.getBadgeLabelAndIcon(location))
              )
              .filter((item: BlockOfferData) => this.flightPriceFilter(item, offers, this.tripType))
              .reduce((locations, loc) => {
                // combine same locations but keeps the original value if new value is null/undefined
                locations.set(loc.destination, {
                  ...locations.get(loc.destination),
                  ...Object.fromEntries(
                    Object.entries(loc).map(([key, value]) => [key, value ?? locations.get(loc.destination)?.[key]])
                  ),
                });
                return locations;
              }, new Map());
            const filteredDestinationList = [...filteredDestination.values()];
            return filteredDestinationList.slice(0, this.getAmountOfOffers(filteredDestinationList));
          }),
          // fallback to CMS content if the recommendation api fails to deliver
          switchMap((locations: BlockOfferData[]) =>
            locations?.length > 0
              ? of(locations)
              : this.languageService.lang.pipe(
                  map((lang) => this.content.items.map((offer) => toPromoBlock(offer as CmsOffer, lang))),
                  map((offs) => offs.slice(0, this.getAmountOfOffers(offs)))
                )
          ),
          switchMap((offs) =>
            this.languageService.localeValue.includes('FI')
              ? getOfferWithAmDestination(
                  offs,
                  this.amDestinationService.amDestinationList(),
                  this.locationRouteCffService
                )
              : of(offs)
          ),
          distinctUntilChanged(),
          finShare()
        )
      ),
      finShare()
    );
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platform)) {
      const debounceScroll$ = this.scrollEvents$.pipe(debounceTime(this.scrollDebounceTime));

      const scrollEventStream$ = this.scrollEvents$.pipe(
        buffer(debounceScroll$),
        take(1),
        withLatestFrom(this.isPersonalizedDestinationsTestVariant$),
        map(([events, isVariantB]) => ({
          action: ElementActions.SCROLL,
          isVariantB,
          offers: events.map(([offer]) => offer.destination),
        })),
        distinctUntilChanged(isDeepEqual),
        finShare()
      );

      const offersStream$ = this.offers$.pipe(
        filter((offers) => !isEmpty(offers)),
        withLatestFrom(this.isPersonalizedDestinationsTestVariant$),
        map(([offers, isVariantB]) => ({
          action: ElementActions.VIEW,
          isVariantB,
          offers: offers.map((offer) => offer.destination),
        })),
        distinctUntilChanged(isDeepEqual),
        finShare()
      );

      this.subscriptions.add(
        merge(scrollEventStream$, offersStream$).subscribe(({ action, isVariantB, offers }) => {
          const gtmContext = isVariantB ? GaContext.DC_PERSONALIZATION : GaContext.TRENDING_DESTINATIONS;
          this.gtmService.trackElement(gtmContext, 'front-page', undefined, offers.join(','), action);
        })
      );
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  destinationClick(selectedOriginLocationCode: string, [destinationItem, index]: [BlockOffer, number]): void {
    this.gtmService.internalPromotion([this.toPromotionItem(destinationItem, index)], PromotionType.CLICK);
    this.dataCloudService.destinationListDestinationClick(
      selectedOriginLocationCode,
      destinationItem.destination,
      DestinationClickTarget.DESTINATION_PAGE
    );
  }

  openAmContinueModal(
    selectedOriginLocationCode: string,
    { amLink, destinationTitle, destinationCode }: { amLink: string; destinationTitle: string; destinationCode: string }
  ): void {
    this.gtmService.trackElement(
      `tile-${destinationTitle.toLowerCase().replace(/ /g, '_')}-flight-hotel`,
      GaContext.CONTENT,
      ElementTypes.LIST_ITEM,
      undefined,
      ElementActions.CLICK
    );
    this.dataCloudService.destinationListDestinationClick(
      selectedOriginLocationCode,
      destinationCode,
      DestinationClickTarget.FLIGHT_PLUS_HOTEL
    );
    this.isAmContinueModalOpen = true;
    this.flightHotelUrl = amLink;
  }

  closeAmContinueModal(): void {
    this.isAmContinueModalOpen = false;
  }

  getTripType(): TripType {
    const localTripType = this.content.localSettings?.tripType as string | undefined;
    const tripType = TripType[localTripType?.toUpperCase()] ?? TripType.RETURN;
    return this.configService.cfg.isPersonalisedDestinationsEnabled
      ? TripType.RETURN
      : this.languageService.localeValue.includes('FI')
        ? TripType.ONEWAY
        : tripType;
  }

  calendarClicked(
    selectedOriginLocationCode: string,
    [event, destinationItem, travelClass, tripType]: [Event, BlockOffer, GlobalBookingTravelClass, TripType]
  ): void {
    stopPropagation(event);
    this.priceCalendarService.openPriceCalendar(
      selectedOriginLocationCode,
      destinationItem.destination,
      tripType,
      travelClass,
      destinationItem.title,
      destinationItem.discountCode
    );

    this.dataCloudService.destinationListDestinationClick(
      selectedOriginLocationCode,
      destinationItem.destination,
      DestinationClickTarget.CALENDAR
    );
  }

  priceCalendarCTAClicked(params: PriceCalendarCTAParams): void {
    this.priceCalendarService.priceCalendarCTAClicked(params);
  }

  closePriceCalendar(): void {
    this.priceCalendarService.closePriceCalendar();
  }

  private getPersonalizedDestinations(origin: string): Observable<PersonalizedDestinationLocation[]> {
    return this.recommendationService.getPersonalizedDestinations(origin).pipe(
      map((locations) => (isPlatformServer(this.platform) ? [] : locations)),
      catchError(() => of([]))
    );
  }

  // filter out the destination where oneway price is more expensive than return
  private flightPriceFilter(location: BlockOfferData, offers: MarketingOffer[], tripType: TripType): BlockOfferData {
    const priceOffer = this.getPriceOffer(offers, location);
    //skip checking if the destination doesn't have price available

    if (!priceOffer?.find((i) => i.price.tripType === tripType)?.price?.amount) {
      return null;
    }

    // do not check price if trending destination type is return
    if (tripType === TripType.RETURN) {
      return location;
    }

    if (
      Number(priceOffer?.find((i) => i.price.tripType === TripType.ONEWAY)?.price.amount) <
      Number(priceOffer?.find((i) => i.price.tripType === TripType.RETURN)?.price.amount)
    ) {
      return location;
    }
  }

  private getPriceOffer(offers: MarketingOffer[], location: BlockOfferData): MarketingOfferPrices[] {
    return offers
      .find((offer) => offer.destination === location.destination)
      ?.prices.filter((priceOffer) => priceOffer.travelClass === GlobalBookingTravelClass.ECONOMY);
  }

  private getAmountOfOffers(destination: BlockOfferData[]): number {
    const amountOfOfferWithPrice = destination.length;
    // If amount offer with price < 3, then no offer will be shown
    if (amountOfOfferWithPrice < this.minNumberOfOffers) {
      return 0;
    }
    // If amount offer with price < 8 and >=4, then 4 offers will be returned
    if (amountOfOfferWithPrice < this.maxNumberOfOffers) {
      return this.minNumberOfOffers;
    }
    // others return 9 offers
    return this.maxNumberOfOffers;
  }

  private getBadgeLabelAndIcon(location: TrendingDestinationLocation): [string, SvgLibraryIcon] {
    return !location.isIncludedInCampaign && location.trend >= this.popularTrendLimit
      ? [TRENDING_BADGE_LABEL, SvgLibraryIcon.TRENDING_ARROW_UP]
      : [null, null];
  }

  private trendingToOffer(
    location: TrendingDestinationLocation | PersonalizedDestinationLocation,
    badgeLabel: string,
    badgeIcon: SvgLibraryIcon
  ): BlockOfferData {
    return {
      title: location.cityName || location.title,
      price: null,
      subtitle: null,
      callToAction: null,
      link: location.destinationUrl,
      flag: location.countryCode,
      imageData: location.picture,
      destination: location.locationCityCode ?? location.locationCode, //destination in marketing offer is locationCityCode if that is available (e.g. REK)
      badgeLabel: location.badgeLabel ?? badgeLabel,
      badgeIcon,
      enableBlackTeaser: location.enableBlackTeaser,
    } as BlockOfferData;
  }

  private toPromotionItem(offer: BlockOfferData, index: number): PromotionItem {
    return {
      position: `${index}`,
      id: 'trending-destinations',
      product: offer.destination,
      type: 'dynamic',
    };
  }
}
